/**
 * What can't be listed on Sellwild. pages/prohibited-items.tsx renders it.
 *
 * The Jev moderation questions in utils/listing-judge.ts (MODERATION_CHECKS)
 * ask about the same things, and testing/jest/data/prohibited-items.test.ts
 * fails if a rule here has no question there, or the other way round. Change
 * both together: a rule the page states and the judge never asks is a promise
 * nobody keeps.
 */
export interface ProhibitedRule {
  /** Stable key the sync test maps to a judge question. */
  readonly id: string;
  readonly name: string;
  readonly examples: string;
  /** The look-alikes that are fine, so an honest seller isn't scared off. */
  readonly allowed?: string;
}

export const PROHIBITED_ITEMS: readonly ProhibitedRule[] = [
  {
    id: 'weapons',
    name: 'Weapons and ammunition',
    examples: 'Guns, gun parts, ammunition and explosives.',
    allowed: 'Toy guns, and knives sold as kitchenware or tools.',
  },
  {
    id: 'drugs',
    name: 'Drugs',
    examples: 'Illegal drugs, and pipes, bongs or other gear made for using them.',
  },
  {
    id: 'prescription',
    name: 'Prescription medicine',
    examples: 'Any medicine that needs a prescription.',
  },
  {
    id: 'alcohol-tobacco',
    name: 'Alcohol and tobacco',
    examples: 'Beer, wine, liquor, cigarettes, cigars and loose tobacco.',
    allowed: 'Empty bottles and collectible packaging.',
  },
  {
    id: 'animals',
    name: 'Live animals',
    examples: 'Pets and other animals for sale.',
    allowed: 'Pet supplies, and adoption listings from our shelter partners.',
  },
  {
    id: 'adult',
    name: 'Adult content',
    examples: 'Sexually explicit photos, videos and other material.',
  },
  {
    id: 'hazardous',
    name: 'Recalled or dangerous goods',
    examples: 'Products under a safety recall, and hazardous materials like fireworks, fuel or pressurized tanks.',
  },
  {
    id: 'stolen',
    name: 'Stolen goods',
    examples: "Anything that isn't yours to sell.",
  },
  {
    id: 'counterfeit-ids',
    name: 'Fake money and IDs',
    examples: "Counterfeit money, and fake IDs, passports or driver's licenses.",
  },
  {
    id: 'hate',
    name: 'Hate symbols',
    examples: 'Nazi and KKK items, swastikas, Confederate flags, and racist slogans, imagery or caricatures.',
  },
  {
    id: 'replicas',
    name: 'Fakes and replicas',
    examples: 'Knockoffs of brand-name items sold as the real thing.',
    allowed: "Unbranded items, and costume pieces that don't claim to be the brand.",
  },
];

export const PROHIBITED_LISTING_CONTENT: readonly ProhibitedRule[] = [
  {
    id: 'off-platform',
    name: 'Contact info or outside payment',
    examples:
      'Phone numbers, emails, WhatsApp or Telegram handles, links to buy somewhere else, or asking to be paid outside Sellwild. Buyers pay and message through Sellwild.',
  },
  {
    id: 'not-a-listing',
    name: "Anything that isn't an item for sale",
    examples: 'Ads for a service or a website, wanted posts, and listings with no real item in them.',
  },
];
